// Percorso file: src/components/games/CampoPartita.jsx
//
// IL CAMPO DAL VIVO — cantiere GAMES, fetta G2, PIANO §2 fasi 3-5 e §3
// «Campo e giocatori». Componente di PRESENTAZIONE: lo stato della partita
// vive nel padre (GamePreparePage) e da qui partono solo le intenzioni del
// coach — entra, esce, sposta, scambia, tocca. È l'unico pezzo dell'app
// senza precedente: i pattern sotto sono INVENTATI qui e dichiarati nel
// referto G2.
//
// IL GESTO (PIANO §3, un solo pattern per touch e mouse):
//   · TOCCO SINGOLO su un giocatore → `onTocco(id)` (il padre apre il menù
//     gol / giallo / rosso);
//   · TRASCINAMENTO, a tre esiti letti al rilascio:
//       lasciato SOPRA un altro token → `onScambia(trascinato, sotto)`;
//       lasciato su un punto LIBERO del campo → `onEntra(id, {x, y})`
//         (se era già in campo è solo uno spostamento: `onSposta`);
//       lasciato FUORI dal campo → `onEsce(id)` (nessun rimpiazzo).
//   Tocco e trascinamento non si confondono: si diventa «trascinamento»
//   solo oltre una soglia di 8px dal punto di pressione; sotto la soglia il
//   rilascio è un tocco.
//   (G3) L'ESPULSO si tocca ma non si trascina: il suo token nel riquadro
//   «Sent off» apre il menù (che gli offre il solo «Clean») con un click
//   semplice, senza il gesto dei Pointer Events.
//
// (G3) IL PRATO porta le due aree di rigore: un `<div>` di sfondo,
// trasparente al puntatore e sotto i token — decorazione, non bersaglio.
//
// ⚠️ POINTER EVENTS, NON L'HTML5 DRAG NATIVO: in package.json non c'è alcuna
// libreria di drag & drop, e `draggable`/`ondragstart` su touch non regge
// (su iOS non parte proprio). I Pointer Events unificano dito, mouse e
// penna: `setPointerCapture` sul token fa arrivare a lui anche i move/up
// che avvengono fuori dal suo rettangolo, e `touch-action: none` (nel CSS)
// impedisce al browser di prendersi il gesto come scorrimento della pagina.
// Il bersaglio del rilascio si legge con `document.elementFromPoint`: il
// token d'origine, mentre lo si trascina, è `pointer-events: none`, così
// sotto il dito si vede l'altro token o il prato. Per la stessa ragione
// move/up/cancel NON si ascoltano sul token ma su `window`, per la sola
// durata del gesto: un elemento reso trasparente al puntatore a metà gesto
// non è un bersaglio su cui contare in ogni browser, `window` sì (gli
// eventi vi risalgono comunque, da qualunque bersaglio). La cattura resta
// come aiuto in più, non come fondamento.
//
// LA POSIZIONE È SOLO VISIVA (PIANO §3): frazioni 0..1 del rettangolo,
// tenute nello stato del padre e MAI salvate.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaFutbol } from "react-icons/fa";
import { eEspulso, eInCampo, eInPanchina, gialliDi, TIPI_EVENTO } from "./regolePartita";
import "./GamesMatch.css";

/** Oltre questa distanza (px) dal punto di pressione il gesto è un
 *  trascinamento; sotto, un tocco. */
const SOGLIA_TRASCINAMENTO = 8;

/** Il punto scelto è tenuto un po' dentro il bordo, perché il token non
 *  finisca mezzo fuori dal rettangolo. */
const MARGINE = 0.08;
const stretto = (v) => Math.min(1 - MARGINE, Math.max(MARGINE, v));

export default function CampoPartita({
  giocatori,
  partita,
  onEntra,
  onEsce,
  onSposta,
  onScambia,
  onTocco
}) {
  const campoRef = useRef(null);

  // Il gesto in corso: { id, pointerId, x0, y0, x, y, attivo }. `attivo`
  // scatta oltre la soglia; finché è falso il rilascio è un tocco. Vive
  // nello stato (per disegnare la copia che segue il dito) e in un ref
  // (per leggerlo dai listener su `window` senza chiusure stantie).
  const [gesto, setGesto] = useState(null);
  const gestoRef = useRef(null);
  const aggiornaGesto = useCallback((g) => {
    gestoRef.current = g;
    setGesto(g);
  }, []);

  // Le intenzioni del coach in un ref: i listener su `window` si montano
  // una volta per gesto e leggono sempre l'ultima versione.
  const azioniRef = useRef({});
  azioniRef.current = { partita, onEntra, onEsce, onSposta, onScambia, onTocco };

  const golDi = (id) =>
    partita.eventi.filter((e) => e.playerId === String(id) && e.tipo === TIPI_EVENTO.GOL).length;

  // -----------------------------------------------------------------------
  // I TRE MOMENTI DEL GESTO
  // -----------------------------------------------------------------------
  const inizio = useCallback(
    (e, id) => {
      // Un gesto per volta; solo il tasto principale del mouse (dito e
      // penna passano sempre).
      if (gestoRef.current) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Un browser senza cattura: i listener su window bastano.
      }
      aggiornaGesto({
        id: String(id),
        pointerId: e.pointerId,
        x0: e.clientX,
        y0: e.clientY,
        x: e.clientX,
        y: e.clientY,
        attivo: false
      });
    },
    [aggiornaGesto]
  );

  const movimento = useCallback(
    (e) => {
      const g = gestoRef.current;
      if (!g || g.pointerId !== e.pointerId) return;
      const attivo =
        g.attivo || Math.hypot(e.clientX - g.x0, e.clientY - g.y0) > SOGLIA_TRASCINAMENTO;
      aggiornaGesto({ ...g, x: e.clientX, y: e.clientY, attivo });
    },
    [aggiornaGesto]
  );

  const fine = useCallback(
    (e) => {
      const g = gestoRef.current;
      if (!g || g.pointerId !== e.pointerId) return;
      aggiornaGesto(null);

      const { partita, onEntra, onEsce, onSposta, onScambia, onTocco } = azioniRef.current;

      if (!g.attivo) {
        onTocco(g.id);
        return;
      }

      // Che cosa c'è sotto il dito? Il token d'origine è `pointer-events:
      // none` mentre lo si trascina, quindi non si vede da solo.
      const sotto = document.elementFromPoint(e.clientX, e.clientY);
      const altro = sotto && sotto.closest ? sotto.closest("[data-player-id]") : null;
      const idSotto = altro ? String(altro.getAttribute("data-player-id")) : null;

      if (idSotto && idSotto !== g.id) {
        onScambia(g.id, idSotto);
        return;
      }

      const rettangolo = campoRef.current ? campoRef.current.getBoundingClientRect() : null;
      const dentro =
        rettangolo &&
        e.clientX >= rettangolo.left &&
        e.clientX <= rettangolo.right &&
        e.clientY >= rettangolo.top &&
        e.clientY <= rettangolo.bottom;

      if (dentro && rettangolo.width > 0 && rettangolo.height > 0) {
        const pos = {
          x: stretto((e.clientX - rettangolo.left) / rettangolo.width),
          y: stretto((e.clientY - rettangolo.top) / rettangolo.height)
        };
        if (eInCampo(partita, g.id)) onSposta(g.id, pos);
        else onEntra(g.id, pos);
        return;
      }

      if (eInCampo(partita, g.id)) onEsce(g.id);
    },
    [aggiornaGesto]
  );

  const annulla = useCallback(
    (e) => {
      const g = gestoRef.current;
      if (g && g.pointerId === e.pointerId) aggiornaGesto(null);
    },
    [aggiornaGesto]
  );

  // I listener su `window` vivono quanto il gesto: montati al primo
  // pointerdown, smontati al rilascio (o all'annullamento).
  const gestoAperto = Boolean(gesto);
  useEffect(() => {
    if (!gestoAperto) return undefined;

    window.addEventListener("pointermove", movimento);
    window.addEventListener("pointerup", fine);
    window.addEventListener("pointercancel", annulla);
    return () => {
      window.removeEventListener("pointermove", movimento);
      window.removeEventListener("pointerup", fine);
      window.removeEventListener("pointercancel", annulla);
    };
  }, [gestoAperto, movimento, fine, annulla]);

  // Tastiera: Invio/Spazio aprono il menù del giocatore, che porta anche la
  // via «to pitch / to bench» senza trascinamento.
  const daTastiera = (e, id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onTocco(String(id));
    }
  };

  // -----------------------------------------------------------------------
  // IL TOKEN — lo stesso in campo e in panchina; in campo è assoluto.
  // -----------------------------------------------------------------------
  const token = (g, dentro) => {
    const id = String(g.id);
    const pos = dentro ? partita.campo[id] : null;
    const inTrascinamento = gesto && gesto.attivo && gesto.id === id;
    const gialli = gialliDi(partita, id);
    const gol = golDi(id);

    return (
      <div
        key={id}
        role="button"
        tabIndex={0}
        data-player-id={id}
        className={
          "qpsc-games-token" +
          (dentro ? " qpsc-games-token--pitch" : "") +
          (inTrascinamento ? " qpsc-games-token--dragging" : "")
        }
        style={dentro ? { left: `${pos.x * 100}%`, top: `${pos.y * 100}%` } : undefined}
        title={g.nome}
        aria-label={`${g.nome}${dentro ? ", on pitch" : ", on bench"}`}
        onPointerDown={(e) => inizio(e, id)}
        onKeyDown={(e) => daTastiera(e, id)}
        onContextMenu={(e) => e.preventDefault()}
      >
        <span className="qpsc-games-token__name">{g.breve}</span>
        {(gol > 0 || gialli > 0) && (
          <span className="qpsc-games-token__marks" aria-hidden="true">
            {gol > 0 && (
              <>
                <FaFutbol />
                {gol > 1 ? gol : null}
              </>
            )}
            {gialli > 0 && <span className="qpsc-games-card-mark qpsc-games-card-mark--yellow" />}
          </span>
        )}
      </div>
    );
  };

  const inCampo = giocatori.filter((g) => eInCampo(partita, g.id));
  const inPanchina = giocatori.filter((g) => eInPanchina(partita, g.id));
  const espulsi = giocatori.filter((g) => eEspulso(partita, g.id));
  const trascinato = gesto && gesto.attivo ? giocatori.find((g) => String(g.id) === gesto.id) : null;

  return (
    <section className="qpsc-games-field" aria-label="Pitch and bench">
      <p className="qpsc-games-field__hint">
        Drag a player onto the pitch to bring them on, off the pitch to take them off,
        onto another player to swap. Tap a player for goals and cards.
      </p>

      <div className="qpsc-games-pitch" ref={campoRef}>
        {/* (G3) Le due aree di rigore: sfondo puramente visivo, trasparente
            al puntatore e sotto i token (vedi GamesMatch.css). */}
        <div className="qpsc-games-pitch__areas" aria-hidden="true" />
        {inCampo.length === 0 && (
          <p className="qpsc-games-pitch__empty">Pitch is empty — drag players here.</p>
        )}
        {inCampo.map((g) => token(g, true))}
      </div>

      <div className="qpsc-games-bench">
        <p className="qpsc-games-bench__title">Bench ({inPanchina.length})</p>
        {inPanchina.length === 0 ? (
          <p className="qpsc-games-bench__empty">Nobody on the bench.</p>
        ) : (
          <div className="qpsc-games-bench__list">{inPanchina.map((g) => token(g, false))}</div>
        )}
      </div>

      {/* (G3) L'espulso NON si trascina (le regole lo respingono comunque)
          ma SI TOCCA: il menù gli offre il solo «Clean», che azzera i suoi
          eventi e lo rimette fra i disponibili se il rosso era un errore. */}
      {espulsi.length > 0 && (
        <div className="qpsc-games-bench qpsc-games-bench--sentoff">
          <p className="qpsc-games-bench__title">Sent off ({espulsi.length})</p>
          <div className="qpsc-games-bench__list">
            {espulsi.map((g) => (
              <div
                key={String(g.id)}
                role="button"
                tabIndex={0}
                className="qpsc-games-token"
                title={g.nome}
                aria-label={`${g.nome}, sent off`}
                onClick={() => onTocco(String(g.id))}
                onKeyDown={(e) => daTastiera(e, g.id)}
                onContextMenu={(e) => e.preventDefault()}
              >
                <span className="qpsc-games-token__name">{g.breve}</span>
                <span className="qpsc-games-token__marks" aria-hidden="true">
                  <span className="qpsc-games-card-mark qpsc-games-card-mark--red" />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* La copia che segue il dito, sopra tutto e trasparente al puntatore. */}
      {trascinato && (
        <div
          className="qpsc-games-token qpsc-games-token--ghost"
          style={{ left: gesto.x, top: gesto.y }}
          aria-hidden="true"
        >
          <span className="qpsc-games-token__name">{trascinato.breve}</span>
        </div>
      )}
    </section>
  );
}
